import { useState, useMemo, useCallback } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import {
  getEligibilityAssessmentStatusLabel,
  ELIGIBILITY_ASSESSMENT_STATUS,
} from 'src/pages/facesheet/_id/eligibility/utils';

const PAGE_SIZE = 10;

export const STATUS_FILTER_OPTIONS = [
  { label: 'Draft', value: ELIGIBILITY_ASSESSMENT_STATUS.DRAFT },
  { label: 'Complete', value: ELIGIBILITY_ASSESSMENT_STATUS.COMPLETE },
  { label: 'Declined', value: ELIGIBILITY_ASSESSMENT_STATUS.DECLINED },
  { label: 'Outreach Failed', value: 'outreach_failed' },
];

const getEffectiveStatus = (assessment) => {
  const label = getEligibilityAssessmentStatusLabel(
    assessment.status,
    assessment.outreach_status,
  );
  return label === 'Outreach Failed' ? 'outreach_failed' : label;
};

const getSortableDate = (assessment) => {
  const isDraft = assessment.status === ELIGIBILITY_ASSESSMENT_STATUS.DRAFT;
  const dateStr = isDraft ? assessment.updated_at : assessment.status_at;
  return dateStr ? new Date(dateStr).getTime() : 0;
};

const getAssessmentMonthYear = (assessment) => {
  const isDraft = assessment.status === ELIGIBILITY_ASSESSMENT_STATUS.DRAFT;
  const dateStr = isDraft ? assessment.updated_at : assessment.status_at;
  if (!dateStr) return null;
  return moment(dateStr).format('MMMM YYYY');
};

const getAssessmentName = (assessment) => get(assessment, 'template.name', '') || 'Eligibility Assessment';

const buildUniqueOptions = (items, accessor) => {
  const unique = [...new Set(items.map(accessor).filter(Boolean))];
  return unique.map((val) => ({ label: val, value: val }));
};

const applyFilters = (assessments, filters) => {
  let result = [...assessments];

  if (filters.status.length > 0) {
    result = result.filter((a) => filters.status.includes(getEffectiveStatus(a)));
  }
  if (filters.organization.length > 0) {
    result = result.filter((a) => filters.organization.includes(get(a, 'provider.name', '')));
  }
  if (filters.name.length > 0) {
    result = result.filter((a) => filters.name.includes(getAssessmentName(a)));
  }
  if (filters.date.length > 0) {
    result = result.filter((a) => {
      const monthYear = getAssessmentMonthYear(a);
      return monthYear && filters.date.includes(monthYear);
    });
  }

  return result;
};

const sortAssessments = (assessments, sortDirection) => assessments.sort((a, b) => {
  const dateA = getSortableDate(a);
  const dateB = getSortableDate(b);
  return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
});

export const useAssessmentFiltering = (assessments) => {
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState([]);
  const [orgFilter, setOrgFilter] = useState([]);
  const [nameFilter, setNameFilter] = useState([]);
  const [dateFilter, setDateFilter] = useState([]);

  const hasFilters = statusFilter.length > 0 ||
    orgFilter.length > 0 ||
    nameFilter.length > 0 ||
    dateFilter.length > 0;

  const filters = {
    status: statusFilter, organization: orgFilter, name: nameFilter, date: dateFilter,
  };

  const orgFilterOptions = useMemo(
    () => (assessments ? buildUniqueOptions(assessments, (a) => get(a, 'provider.name', '')) : []),
    [assessments],
  );

  const nameFilterOptions = useMemo(
    () => (assessments ? buildUniqueOptions(assessments, getAssessmentName) : []),
    [assessments],
  );

  const dateFilterOptions = useMemo(
    () => (assessments ? buildUniqueOptions(assessments, getAssessmentMonthYear) : []),
    [assessments],
  );

  const filteredAndSorted = useMemo(() => {
    if (!assessments) return [];
    const filtered = applyFilters(assessments, filters);
    return sortAssessments(filtered, sortDirection);
  }, [assessments, statusFilter, orgFilter, nameFilter, dateFilter, sortBy, sortDirection]);

  const totalCount = filteredAndSorted.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paginatedAssessments = filteredAndSorted.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const paging = {
    current_page: page,
    total_count: totalCount,
    total_pages: totalPages,
    per: PAGE_SIZE,
  };

  const onSortChange = useCallback((column, direction) => {
    const newDirection = direction || (sortBy === column && sortDirection === 'asc' ? 'desc' : 'asc');
    setSortBy(column);
    setSortDirection(newDirection);
    setPage(1);
  }, [sortBy, sortDirection]);

  const filterSetters = {
    status: setStatusFilter,
    organization: setOrgFilter,
    name: setNameFilter,
    date: setDateFilter,
  };

  const onFiltersChange = useCallback((filterKey, values) => {
    const setter = filterSetters[filterKey];
    if (setter) setter(values);
    setPage(1);
  }, []);

  const onNextClick = useCallback(() => setPage((p) => Math.min(p + 1, totalPages)), [totalPages]);
  const onPrevClick = useCallback(() => setPage((p) => Math.max(p - 1, 1)), []);

  return {
    sortBy,
    sortDirection,
    hasFilters,
    paginatedAssessments,
    totalCount,
    paging,
    orgFilterOptions,
    nameFilterOptions,
    dateFilterOptions,
    onSortChange,
    onFiltersChange,
    onNextClick,
    onPrevClick,
  };
};
